import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';

import {
    Loader2,
    TrendingUp,
    Package,
    ShoppingCart,
    Users,
    Wallet,
    Award,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { dashboardAPI, customersAPI } from '../services/api';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from 'recharts';

type Period = 'day' | 'week' | 'month' | 'year' | 'all';

/* ---------- Types ---------- */
type Summary = {
    total_income?: string;
    wallet_balance?: string;
    license_count?: number;
    buyer_count?: number;
    total_gross_sales?: string;
    total_commission?: string;
};

type ChartPoint = {
    date: string;
    total_sales: string;
};

type DashboardPayload = {
    summary?: Summary;
    chart?: ChartPoint[];
};

type LeaderboardEntry = {
    agent_id: string;
    items_sold: number;
    gross: string;
    commission: string;
    rnk: string | number;
    first_name?: string | null;
    last_name?: string | null;
};

type LeaderboardPayload =
    | {
    period: { days: number };
    top5: LeaderboardEntry[];
    self: LeaderboardEntry;
    last: LeaderboardEntry;
}
    | {
    period: Period;
    top5: LeaderboardEntry[];
    self: LeaderboardEntry;
    last: LeaderboardEntry;
};

const fa = new Intl.NumberFormat('fa-IR');

const periodLabel = (p: Period) => {
    switch (p) {
        case 'day':
            return 'روز';
        case 'week':
            return 'هفته';
        case 'month':
            return 'ماه';
        case 'year':
            return 'سال';
        default:
            return 'همه';
    }
};

const Dashboard: React.FC = () => {
    const { agent } = useAuth();
    const [period, setPeriod] = useState<Period>('month');
    const isPending = agent?.is_approved === false;

    const [data, setData] = useState<DashboardPayload | null>(null);
    const [leaderboard, setLeaderboard] = useState<LeaderboardPayload | null>(null);

    const [loading, setLoading] = useState(true);
    const [lbLoading, setLbLoading] = useState(true);
    const [error, setError] = useState('');
    const [lbError, setLbError] = useState('');

    useEffect(() => {
        fetchDashboardData(period);
    }, [period]);

    useEffect(() => {
        fetchLeaderboard(period);
    }, [period]);

    const fetchDashboardData = async (prd: Period) => {
        setLoading(true);
        setError('');
        try {
            const dashboardData = await dashboardAPI.getDashboardData(prd);
            setData(dashboardData);
        } catch (err: any) {
            const errorMessage =
                err?.response?.data?.message || err?.message || 'بارگذاری اطلاعات داشبورد با خطا مواجه شد';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeaderboard = async (prd: Period) => {
        setLbLoading(true);
        setLbError('');
        try {
            const lb = await customersAPI.getLeaderboard(prd);
            setLeaderboard(lb as unknown as LeaderboardPayload);
        } catch (err: any) {
            const msg =
                err?.response?.data?.message || err?.message || 'بارگذاری لیدربورد با خطا مواجه شد';
            setLbError(msg);
        } finally {
            setLbLoading(false);
        }
    };

    /* ---------- مشتقات داده ---------- */
    const licenseCount = data?.summary?.license_count ?? 0;
    const buyerCount = data?.summary?.buyer_count ?? 0;
    const totalIncomeNum = useMemo(() => Number(data?.summary?.total_income ?? 0), [data]);
    const walletBalanceNum = useMemo(() => Number(data?.summary?.wallet_balance ?? 0), [data]);
    const commissionNum = useMemo(() => Number(data?.summary?.total_commission ?? 0), [data]);

    const chartData = useMemo(() => {
        const raw = data?.chart ?? [];
        return raw.map((p) => ({
            date: new Date(p.date).toLocaleDateString('fa-IR'),
            total: Number(p.total_sales ?? 0),
        }));
    }, [data]);

    const totalChartSales = useMemo(
        () => chartData.reduce((sum, p) => sum + (p.total || 0), 0),
        [chartData]
    );

    const statsBase = [
        {
            title: 'کل سفارش‌ها',
            value: fa.format(licenseCount),
            icon: ShoppingCart,
            description: `بازه: ${periodLabel(period)}`,
            gradient: 'from-green-500 to-emerald-500',
            link: '/orders',
        },
        {
            title: 'کل مشتریان',
            value: fa.format(buyerCount),
            icon: Users,
            description: `بازه: ${periodLabel(period)}`,
            gradient: 'from-teal-500 to-cyan-500',
            link: '/customers',
        },
        {
            // 👇 فقط کمیسیون؛ عنوان = "درآمد"
            title: 'درآمد',
            value: `${fa.format(commissionNum / 10)} تومان`,
            icon: TrendingUp,
            description: `بر اساس کمیسیون (${periodLabel(period)})`,
            gradient: 'from-emerald-500 to-teal-500',
            link: '/wallet',
        },
        {
            // 👇 فقط کمیسیون؛ عنوان = "درآمد"
            title: 'موجودی کیف‌پول',
            value: `${fa.format(walletBalanceNum / 10)} تومان`,
            icon: Wallet,
            description: `واریزی شما`,
            gradient: 'from-emerald-500 to-teal-500',
            link: '/wallet',
        },
    ];

    // دیگه extraStats نداریم (مجموع فروش حذف شد + کمیسیون رفت تو statsBase)
    const extraStats: typeof statsBase = [];

    /* ---------- Loading / Error ---------- */
    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <div className="text-center">
                    <Loader2 className="h-10 w-10 animate-spin text-green-600 mx-auto mb-3" />
                    <p className="text-green-700 font-medium">در حال بارگذاری داشبورد...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4"
                dir="rtl"
            >
                <Alert variant="destructive" className="max-w-xl w-full">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            </div>
        );
    }

    /* ---------- UI ---------- */
    return (
        <div
            className="space-y-6 p-4 md:p-8 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 min-h-screen"
            dir="rtl"
        >
            {/* هدر گرادیانی + انتخاب بازه */}
            {/* ---------------------- HEADER ---------------------- */}
            <div className="p-5 md:p-8 rounded-3xl bg-gradient-to-r from-green-700 to-emerald-600 text-white shadow-xl">
                <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                    ✨
                    <span>خوش آمدید</span>
                </h1>
                <p className="text-green-100 mt-2 text-xs md:text-sm">
                    خلاصه وضعیت خرید و مالی شما در لایسنس‌اپ
                </p>
            </div>

            {/* ---------------------- PERIOD FILTER ---------------------- */}
            <div className="bg-white/90 dark:bg-slate-900/90 shadow-md rounded-2xl border border-green-100 dark:border-slate-700 px-2 py-2">
                <div className="flex items-center justify-between gap-3">

    <span className="text-xs hidden sm:block md:text-sm text-green-900 dark:text-emerald-100 font-medium">
      بازه زمانی گزارش
    </span>

                    <div className="flex-1 w-full md:w-auto overflow-x-auto scrollbar-none">
                        <div className="flex gap-1.5 md:gap-2 justify-between md:justify-end min-w-max">

                            {(['day','week','month','year','all'] as Period[]).map((p) => (
                                <button
                                    key={p}
                                    onClick={() => setPeriod(p)}
                                    className={[
                                        'px-3.5 md:px-3.5 py-1.5 rounded-full text-[11px] md:text-sm transition font-medium whitespace-nowrap',
                                        period === p
                                            ? 'bg-green-600 text-white shadow'
                                            : 'text-green-700 dark:text-emerald-100 hover:bg-green-50 dark:hover:bg-slate-800'
                                    ].join(' ')}
                                >
                                    {periodLabel(p)}
                                </button>
                            ))}

                        </div>
                    </div>

                </div>
            </div>
            {/* بالا، بعد از هدر گرادیانی */}
            {isPending && (
                <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                    <AlertDescription>
                        درخواست نمایندگی شما ثبت شده اما هنوز تایید نشده است.
                        <br />
                        <span className="font-semibold">
                تا تأیید نشوید، دسترسی به بخش‌های مختلف سیستم برایتان غیر فعال است.
            </span>
                    </AlertDescription>
                </Alert>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {statsBase.map((stat, index) => {
                    const Icon = stat.icon;
                    const content = (
                        <Card
                            className={[
                            "transition-all duration-300 border-green-200 bg-white/95 backdrop-blur",
                            isPending
                                ? "opacity-60 cursor-not-allowed"
                                : "hover:shadow-xl hover:scale-[1.02] cursor-pointer group"
                        ].join(' ')}>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-green-800 group-hover:text-green-900">
                                        {stat.title}
                                    </CardTitle>
                                    <div className={`p-3 rounded-full bg-gradient-to-r ${stat.gradient} shadow-lg`}>
                                        <Icon className="h-5 w-5 text-white" />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-xl md:text-2xl font-bold text-green-900 mb-1">
                                        {stat.value}
                                    </div>
                                    <p className="text-xs text-green-600">{stat.description}</p>
                                </CardContent>
                            </Card>
                    );

                    if (isPending) {
                        // بدون لینک، فقط نمایش
                        return (
                            <div key={index}>
                                {content}
                            </div>
                        );
                    }

                    return (
                        <Link key={index} to={stat.link}>
                            {content}
                        </Link>
                    );
                })}
            </div>

            {/* Extra stats (خالی، چون حذف کردیم) */}
            {extraStats.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                    {extraStats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <Link key={`x-${index}`} to={stat.link}>
                                <Card className="hover:shadow-xl transition-all duration-300 border-green-200 hover:scale-[1.02] cursor-pointer group bg-white/95 backdrop-blur">
                                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                        <CardTitle className="text-sm font-medium text-green-800 group-hover:text-green-900">
                                            {stat.title}
                                        </CardTitle>
                                        <div className={`p-3 rounded-full bg-gradient-to-r ${stat.gradient} shadow-lg`}>
                                            <Icon className="h-5 w-5 text-white" />
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="text-xl md:text-2xl font-bold text-green-900 mb-1">
                                            {stat.value}
                                        </div>
                                        <p className="text-xs text-green-600">{stat.description}</p>
                                    </CardContent>
                                </Card>
                            </Link>
                        );
                    })}
                </div>
            )}


            {/* Sales Chart */}
            <Card className="border-green-200 bg-white/95 backdrop-blur-sm shadow-md">
                <CardHeader>
                    <CardTitle className="text-green-900">نمودار فروش اخیر</CardTitle>
                    <CardDescription className="text-green-700">
                        مجموع فروش بر اساس تاریخ ({periodLabel(period)})
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {chartData.length === 0 ? (
                        <div className="py-10 text-center text-green-700">
                            داده‌ای برای نمایش نمودار وجود ندارد
                        </div>
                    ) : (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis tickFormatter={(v) => fa.format(v / 10)} />
                                    <Tooltip
                                        formatter={(value: any) => [`${fa.format(value / 10)} تومان`, 'فروش']}
                                        labelFormatter={(label) => `تاریخ: ${label}`}
                                    />
                                    <Line type="monotone" dataKey="total" stroke="#16a34a" strokeWidth={2} dot />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Leaderboard */}
            <Card className="border-green-200 bg-white/95 backdrop-blur-sm shadow-md">
                <CardHeader>
                    <CardTitle className="text-green-900">لیدربورد نمایندگان</CardTitle>
                    <CardDescription className="text-green-700">
                        بازه‌ی زمانی: {periodLabel(period)}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {lbLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="h-6 w-6 animate-spin text-green-600" />
                        </div>
                    ) : lbError ? (
                        <Alert variant="destructive">
                            <AlertDescription>{lbError}</AlertDescription>
                        </Alert>
                    ) : !leaderboard ? (
                        <div className="py-6 text-center text-green-700">
                            داده‌ای برای نمایش لیدربورد موجود نیست
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-right text-sm">
                                <thead>
                                <tr className="bg-green-50 text-green-700">
                                    <th className="py-2 px-3 font-medium">رتبه</th>
                                    <th className="py-2 px-3 font-medium">نماینده</th>
                                    <th className="py-2 px-3 font-medium">تعداد فروش</th>
                                    <th className="py-2 px-3 font-medium">فروش کل</th>
                                    <th className="py-2 px-3 font-medium">کمیسیون کل</th>
                                    <th className="py-2 px-3 font-medium">برچسب</th>
                                </tr>
                                </thead>
                                <tbody>
                                {/* Top 5 */}
                                {leaderboard.top5.map((row) => {
                                    const isSelf =
                                        String(row.agent_id) === String(leaderboard.self.agent_id);
                                    const fullName = [row.first_name, row.last_name]
                                        .filter(Boolean)
                                        .join(' ')
                                        .trim();
                                    return (
                                        <tr
                                            key={`top-${row.agent_id}`}
                                            className="border-b last:border-0 hover:bg-green-50/40"
                                        >
                                            <td className="py-2 px-3">{row.rnk}</td>
                                            <td className="py-2 px-3">
                                                {fullName
                                                    ? `${fullName} (#${row.agent_id})`
                                                    : `نماینده #${row.agent_id}`}
                                            </td>
                                            <td className="py-2 px-3">
                                                {fa.format(row.items_sold)}
                                            </td>
                                            <td className="py-2 px-3">
                                                {fa.format(Number(row.gross) / 10)} تومان
                                            </td>
                                            <td className="py-2 px-3">
                                                {fa.format(Number(row.commission) / 10)} تومان
                                            </td>
                                            <td className="py-2 px-3">
                                                {isSelf && (
                                                    <span className="inline-block rounded-full bg-green-600 text-white text-xs px-2 py-1">
                                                            شما
                                                        </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}

                                {/* آخرین نفر (در صورت متفاوت بودن) */}
                                {(() => {
                                    const shownIds = new Set(
                                        leaderboard.top5.map((x) => String(x.agent_id))
                                    );
                                    shownIds.add(String(leaderboard.self.agent_id));
                                    const lastRow = leaderboard.last;
                                    const shouldShowLast =
                                        lastRow && !shownIds.has(String(lastRow.agent_id));
                                    if (!shouldShowLast) return null;

                                    const row = lastRow!;
                                    const isSelf =
                                        String(row.agent_id) ===
                                        String(leaderboard.self.agent_id);
                                    const fullName = [row.first_name, row.last_name]
                                        .filter(Boolean)
                                        .join(' ')
                                        .trim();

                                    return (
                                        <tr
                                            key={`last-${row.agent_id}`}
                                            className="border-t-2 border-green-200 hover:bg-green-50/40"
                                        >
                                            <td className="py-2 px-3">{row.rnk}</td>
                                            <td className="py-2 px-3">
                                                {fullName
                                                    ? `${fullName} (#${row.agent_id})`
                                                    : `نماینده #${row.agent_id}`}
                                            </td>
                                            <td className="py-2 px-3">
                                                {fa.format(row.items_sold)}
                                            </td>
                                            <td className="py-2 px-3">
                                                {fa.format(Number(row.gross) / 10)} تومان
                                            </td>
                                            <td className="py-2 px-3">
                                                {fa.format(Number(row.commission) / 10)} تومان
                                            </td>
                                            <td className="py-2 px-3 space-x-1 space-x-reverse">
                                                    <span className="inline-block rounded-full bg-gray-200 text-gray-700 text-xs px-2 py-1">
                                                        آخرین
                                                    </span>
                                                {isSelf && (
                                                    <span className="inline-block rounded-full bg-green-600 text-white text-xs px-2 py-1">
                                                            شما
                                                        </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })()}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* فعالیت‌های اخیر (Placeholder) */}
            <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-green-900 flex items-center">
                        <Award className="ml-2 h-6 w-6 text-green-600" />
                        فعالیت‌های اخیر
                    </CardTitle>
                    <CardDescription className="text-green-700">
                        آخرین تراکنش‌ها و به‌روزرسانی‌ها
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-green-600">
                        <div className="bg-gradient-to-r from-green-400 to-emerald-400 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center shadow-md">
                            <Award className="h-8 w-8 text-white" />
                        </div>
                        <p className="text-lg font-medium text-green-800 mb-2">
                            فعلاً فعالیتی ثبت نشده
                        </p>
                        <p className="text-sm text-green-600">
                            با ایجاد سفارش یا مدیریت محصولات شروع کنید
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 mt-6 justify-center">
                            <Link to={isPending ? "#" : "/orders"}>
                                <Button
                                    disabled={isPending}
                                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    ایجاد سفارش
                                </Button>
                            </Link>
                            <Link to={isPending ? "#" : "/products"}>
                                <Button
                                    variant="outline"
                                    disabled={isPending}
                                    className="border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    مشاهده محصولات
                                </Button>
                            </Link>
                        </div>

                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Dashboard;
